import { fetchWithOptions } from 'Utility/fetch';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { TTOPIC } from '../types/TTopic';
import { IDefaultFetcherInterface, queryParamsBuilder } from './util';

export interface ITopicCreate extends IDefaultFetcherInterface {
  name: 'create';
  body: {
    title: string;
    body?: string;
    targetPatients: string[];
    tags: string[];
  };
}
export interface ITopicDelete extends IDefaultFetcherInterface {
  name: 'delete';
}
export interface ITopicList extends IDefaultFetcherInterface {
  name: 'list';
}
export interface ITopicUpdate extends IDefaultFetcherInterface {
  name: 'update';
  body: {
    title?: string;
    body?: string;
    allowThreads?: boolean;
    // targetPatients: string[];
    // tags: string[];
  };
  /** topicid */
  queryParams: [string];
}
export interface ITopicFetch extends IDefaultFetcherInterface {
  name: 'fetch';
  /** patientid, topicid */
  queryParams: [string, string];
}
export interface ITopicActivate extends IDefaultFetcherInterface {
  name: 'activate';
  /** topicid */
  queryParams: [string];
}
export interface ITopicAssign extends IDefaultFetcherInterface {
  name: 'assign';
  /** topicid, userid */
  queryParams: [string, string];
}
export interface ITopicUnassign extends IDefaultFetcherInterface {
  name: 'unassign';
}
export interface ITopicClose extends IDefaultFetcherInterface {
  name: 'close';
  /** topicid, closuretimestamp */
  queryParams: [string, string];
}
export interface ITopicUnclose extends IDefaultFetcherInterface {
  name: 'unclose';
  /** topicid */
  queryParams: [string];
}

type TTopicActions =
  | ITopicCreate
  | ITopicDelete
  | ITopicList
  | ITopicUpdate
  | ITopicFetch
  | ITopicActivate
  | ITopicAssign
  | ITopicUnassign
  | ITopicClose
  | ITopicUnclose;

const basePath = '/api/messaging/v1';

const topicFetchConfig = {
  create: {
    method: 'POST',
    path: basePath + '/topic',
  },
  delete: {
    method: 'DELETE',
    path: basePath + '/topic',
  },
  list: {
    method: 'GET',
    path: basePath + '/topics/org',
  },
  fetch: {
    method: 'GET',
    path: basePath + '/topic',
  },
  update: {
    method: 'PATCH',
    path: basePath + '/topic/content',
  },
  activate: {
    method: 'PATCH',
    path: basePath + '/topic/activate',
  },
  assign: {
    method: 'PATCH',
    path: basePath + '/topic/assign',
  },
  unassign: {
    method: 'PATCH',
    path: basePath + '/topic/unassign',
  },
  close: {
    method: 'PATCH',
    path: basePath + '/topic/close',
  },
  unclose: {
    method: 'PATCH',
    path: basePath + '/topic/unclose',
  },
};

type TDefaultReturn<N> = {
  returnType: N extends 'list' ? TTOPIC[] | false : N extends 'create' | 'update' | 'fetch' ? TTOPIC | false : boolean;
};

export const topicFetchBuilder = <T>(builderProps: TTopicActions): Promise<TDefaultReturn<T>['returnType']> => {
  const actionName = builderProps.name;
  const path =
    topicFetchConfig[actionName].path +
    `${builderProps.queryParams ? queryParamsBuilder(builderProps.queryParams) : ''}`;
  const bodyObject = builderProps.body ? { body: JSON.stringify(builderProps.body) } : {};

  return fetchWithOptions(
    path,
    { neurojwt: parseJWTFromCookie() },
    { method: topicFetchConfig[actionName].method, ...bodyObject },
  )
    .then(res => {
      if (res.status === 200) {
        // Return response body parsed, or true if it cannot be parsed
        return res.json().catch(() => true);
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(
        Loggable.BugActionable,
        err,
        new ActionableErrorContext(null, `${topicFetchConfig[actionName].method} topic`),
      );
      return false;
    });
};
